import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import ClientVerificationPanel from './components/ClientVerificationPanel';
import DuplicateCheckResults from './components/DuplicateCheckResults';
import IdGenerationPanel from './components/IdGenerationPanel';
import GeneratedIdDisplay from './components/GeneratedIdDisplay';
import BatchGenerationPanel from './components/BatchGenerationPanel';
import AuditTrailPanel from './components/AuditTrailPanel';

const UniqueIdGenerationSystem = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('single');
  const [currentStep, setCurrentStep] = useState('verification');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedId, setGeneratedId] = useState(null);
  const [batchProgress, setBatchProgress] = useState({
    current: 0,
    total: 0,
    successful: 0,
    failed: 0,
    pending: 0,
    estimatedTime: '0 Min',
    currentClient: ''
  });

  const mockClientData = {
    clientNumber: 'BA-2024-089456',
    firstName: 'Anna',
    lastName: 'Schmidt',
    fullName: 'Anna Schmidt',
    dateOfBirth: '15.03.1985',
    placeOfBirth: 'Berlin',
    address: 'Hauptstraße 45, 10115 Berlin',
    phone: '+49 30 12345678',
    email: 'anna.schmidt@email.de',
    occupation: 'Softwareentwicklerin',
    photo: "https://img.rocket.new/generatedImages/rocket_gen_img_12474730f-1763296027036.png",
    photoAlt: 'Professional headshot of woman with shoulder-length brown hair wearing navy blue blazer and white blouse in office setting'
  };

  const mockDuplicateResults = {
    matches: [
    {
      id: 1,
      uniqueId: 'BA-2023-045789',
      fullName: 'Anna Schmidt',
      dateOfBirth: '15.03.1985',
      address: 'Hauptstraße 47, 10115 Berlin',
      occupation: 'IT-Beraterin',
      confidenceScore: 85,
      matchingFields: ['Name', 'Geburtsdatum', 'Adresse'],
      photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1158c42da-1763295926268.png",
      photoAlt: 'Professional portrait of woman with brown hair in gray business suit smiling at camera in modern office environment'
    }]

  };

  const mockAuditActivities = [
  {
    id: 1,
    type: 'generated',
    action: 'ID generiert',
    description: 'Neue eindeutige ID für Kunde erstellt',
    user: 'Maria Weber',
    timestamp: '26.11.2025 18:45',
    clientId: 'BA-2025-123456',
    ipAddress: '192.168.1.100',
    notes: 'Reguläre ID-Generierung nach Kundenaufnahme'
  },
  {
    id: 2,
    type: 'verified',
    action: 'Duplikatsprüfung durchgeführt',
    description: 'Keine Duplikate gefunden',
    user: 'Thomas Müller',
    timestamp: '26.11.2025 17:30',
    clientId: 'BA-2025-123455',
    ipAddress: '192.168.1.101'
  },
  {
    id: 3,
    type: 'approved',
    action: 'Stapelgenerierung genehmigt',
    description: '25 IDs für Stapelverarbeitung freigegeben',
    user: 'Dr. Sarah Fischer (Supervisor)',
    timestamp: '26.11.2025 16:15',
    ipAddress: '192.168.1.102',
    notes: 'Genehmigung für Massenimport neuer Kunden aus Außenstelle Hamburg'
  },
  {
    id: 4,
    type: 'exported',
    action: 'Audit-Bericht exportiert',
    description: 'Monatlicher Compliance-Bericht generiert',
    user: 'Michael Becker',
    timestamp: '26.11.2025 15:00',
    ipAddress: '192.168.1.103'
  },
  {
    id: 5,
    type: 'modified',
    action: 'ID-Format aktualisiert',
    description: 'Wechsel von Legacy- zu Standard-Format',
    user: 'Anna Schmidt',
    timestamp: '26.11.2025 14:20',
    clientId: 'BA-2025-123454',
    ipAddress: '192.168.1.104',
    notes: 'Anpassung an neue Bundesrichtlinien'
  }];


  const handleVerifyClient = () => {
    setCurrentStep('duplicate-check');
  };

  const handleDuplicateCheckProceed = () => {
    setCurrentStep('generation');
  };

  const handleGenerate = (config) => {
    setIsGenerating(true);
    setTimeout(() => {
      setGeneratedId({
        id: 'BA-2025-123789',
        format: 'Standard-Format',
        checksum: 'A7B2',
        createdAt: '26.11.2025 19:04',
        createdBy: 'Maria Weber',
        notes: config?.notes || 'Reguläre ID-Generierung'
      });
      setIsGenerating(false);
      setCurrentStep('display');
    }, 2000);
  };

  const handleBatchGenerate = (selectedClients) => {
    setIsGenerating(true);
    setBatchProgress({
      current: 0,
      total: selectedClients?.length,
      successful: 0,
      failed: 0,
      pending: selectedClients?.length,
      estimatedTime: `${selectedClients?.length * 2} Sek`,
      currentClient: 'Anna Schmidt'
    });

    let current = 0;
    const interval = setInterval(() => {
      current++;
      setBatchProgress((prev) => ({
        ...prev,
        current,
        successful: current,
        pending: selectedClients?.length - current,
        currentClient: `Kunde ${current} von ${selectedClients?.length}`
      }));

      if (current >= selectedClients?.length) {
        clearInterval(interval);
        setIsGenerating(false);
      }
    }, 2000);
  };

  const handleAssociate = () => {
    alert('ID erfolgreich mit Kundenprofil verknüpft!');
    setCurrentStep('verification');
    setGeneratedId(null);
  };

  const handleCloseDisplay = () => {
    setCurrentStep('verification');
    setGeneratedId(null);
  };

  const tabs = [
  { id: 'single', label: 'Einzelgenerierung', icon: 'User' },
  { id: 'batch', label: 'Stapelverarbeitung', icon: 'Layers' },
  { id: 'audit', label: 'Audit-Trail', icon: 'FileText' }];


  return (
    <>
      <Helmet>
        <title>Eindeutige ID-Generierung | Bundesagentur für Arbeit</title>
        <meta name="description" content="Sichere Generierung und Verwaltung eindeutiger Kunden-IDs für die Bundesagentur für Arbeit" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <Header sidebarCollapsed={sidebarCollapsed} />

        <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="p-6 max-w-[1600px] mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Fingerprint" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Eindeutige ID-Generierung</h1>
                  <p className="text-sm text-muted-foreground mt-1">Sichere Erstellung und Verwaltung von Kunden-Identifikatoren</p>
                </div>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-4 gap-6 mb-8">
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircle" size={24} color="var(--color-success)" />
                  </div>
                  <span className="text-xs font-medium text-success">+12% heute</span>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-1">1.247</h3>
                <p className="text-sm text-muted-foreground">Heute generiert</p>
              </div>

              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Database" size={24} color="var(--color-primary)" />
                  </div>
                  <span className="text-xs font-medium text-primary">Aktiv</span>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-1">98.456</h3>
                <p className="text-sm text-muted-foreground">Gesamt IDs</p>
              </div>

              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="Clock" size={24} color="var(--color-warning)" />
                  </div>
                  <span className="text-xs font-medium text-warning">Ausstehend</span>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-1">23</h3>
                <p className="text-sm text-muted-foreground">Warteschlange</p>
              </div>

              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Zap" size={24} color="var(--color-accent)" />
                  </div>
                  <span className="text-xs font-medium text-accent">Durchschnitt</span>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-1">1,8s</h3>
                <p className="text-sm text-muted-foreground">Generierungszeit</p>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-card rounded-lg border border-border mb-6">
              <div className="flex border-b border-border">
                {tabs?.map((tab) =>
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors relative ${
                  activeTab === tab?.id ?
                  'text-primary' : 'text-muted-foreground hover:text-foreground'}`
                  }>

                    <Icon name={tab?.icon} size={20} />
                    <span>{tab?.label}</span>
                    {activeTab === tab?.id &&
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  }
                  </button>
                )}
              </div>
            </div>

            {/* Content Area */}
            {activeTab === 'single' &&
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  {currentStep === 'verification' &&
                <ClientVerificationPanel
                  clientData={mockClientData}
                  onVerify={handleVerifyClient}
                  onCancel={() => {}} />

                }

                  {currentStep === 'duplicate-check' &&
                <DuplicateCheckResults
                  results={mockDuplicateResults}
                  onSelectMatch={(match) => console.log('Selected match:', match)}
                  onProceed={handleDuplicateCheckProceed} />

                }

                  {currentStep === 'generation' &&
                <IdGenerationPanel
                  onGenerate={handleGenerate}
                  isGenerating={isGenerating} />

                }

                  {currentStep === 'display' && generatedId &&
                <GeneratedIdDisplay
                  generatedId={generatedId}
                  onClose={handleCloseDisplay}
                  onAssociate={handleAssociate} />

                }
                </div>

                <div>
                  <AuditTrailPanel activities={mockAuditActivities?.slice(0, 5)} />
                </div>
              </div>
            }

            {activeTab === 'batch' &&
            <div className="grid grid-cols-2 gap-6">
                <BatchGenerationPanel
                onGenerate={handleBatchGenerate}
                isGenerating={isGenerating}
                progress={batchProgress} />

                <div>
                  <AuditTrailPanel activities={mockAuditActivities?.filter((a) => a?.type === 'approved' || a?.type === 'generated')} />
                </div>
              </div>
            }

            {activeTab === 'audit' &&
            <div className="max-w-4xl mx-auto">
                <AuditTrailPanel activities={mockAuditActivities} />
              </div>
            }
          </div>
        </main>
      </div>
    </>);

};

export default UniqueIdGenerationSystem;